import React, { CSSProperties, useEffect, useState } from 'react';
import cls from 'classnames';
import TaskIndicator from '../task_indicator';
import styles from './ribbon.module.scss';
import { EditableHeader, EditableHeaderRef } from '../EditableHeader/EditableHeader';

export type RibbonProps = {
  title?: React.ReactNode;
  titleClassName?: string;
  titleStatic?: boolean;

  editableTitle?: string;

  /**
   * Called when value of `editableTitle` has changed. 
   */
  onTitleChange?: (title: string) => void | Promise<boolean>;

  immediate?: React.ReactNode;
  immediateClassName?: string;
  immediateStyle?: CSSProperties;
  asideBeforePagination?: React.ReactNode;
  aside?: React.ReactNode;
  pagination?: React.ReactNode;
  subtitle?: React.ReactNode;

  /**
   * Displays a task indicator.
   */
  busy?: boolean;
  /**
   * Whether to delay the task indicator, for automatic refreshes.
   */
  busyDelay?: boolean;
  className?: string;
  style?: CSSProperties;
  isCompact?: boolean;
  margin?: 'none';

  editableHeaderController?: (ref: EditableHeaderRef) => void;
};

enum PaginationPosition {
  Compact,
  Aside
}

/**
 * A header with immediate action or actions and optional aside actions (such as search.) 
 */
export const Ribbon: React.FC<RibbonProps> = ({
  title,
  titleClassName,
  editableTitle,
  onTitleChange,
  titleStatic = true,
  subtitle,
  asideBeforePagination,
  aside,
  pagination,
  immediate,
  immediateClassName,
  immediateStyle,
  busy,
  busyDelay,
  className,
  style,
  editableHeaderController,
  isCompact,
  margin,
}) => {
  const [isTitleEditable, setTitleEditable] = useState(false);
  const [isTitleBusy, setTitleBusy] = useState(false);
  const [editedTitle, setEditedTitle] = useState<string>(editableTitle);
  const [isBusyVisible, setBusyVisible] = useState(false);

  useEffect(() => {
    if (busy && busyDelay) {
      const id = setTimeout(() => setBusyVisible(true), 1500);
      return () => { clearTimeout(id); }
    } else {
      setBusyVisible(busy);
    }
  }, [busy, busyDelay]);

  useEffect(() => {
    setEditedTitle(editableTitle?.trim());
  }, [editableTitle]);

  const paginationPosition = window.innerWidth > 1200 ? PaginationPosition.Aside : PaginationPosition.Compact;

  async function saveTitle(title: string) {
    if (isTitleBusy || typeof onTitleChange !== 'function') {
      return;
    }

    setTitleBusy(true);
    const result = await onTitleChange(title);

    if (result !== false) {
      setTitleEditable(false);
    }

    setTitleBusy(false);
  }

  titleClassName = cls([
    styles.title,
    titleStatic ? "transition-preserve" : null,
    titleClassName,
  ]);

  //  Whether to force subtitle element to prevent entire view from moving.
  const forceSubtitleElement = !isCompact && typeof busy === 'boolean';

  return (
    <header
      className={cls([styles.ribbon, isCompact ? styles.compact : null, className])}
      style={style}
      data-margin={margin}>
      {
        editableTitle ? (
          <EditableHeader
            className={titleClassName}
            showTaskIndicator={isTitleBusy}
            editable={isTitleEditable}
            onEditableChange={setTitleEditable}
            value={editedTitle}
            onChange={setEditedTitle}
            onSave={saveTitle}
            controller={editableHeaderController}>
            <div className={styles.immediate}>
              {immediate}
            </div>
          </EditableHeader>
        ) : (
          <h1
            className={titleClassName}
            data-no-sway={isCompact ? "" : undefined}
            data-skeleton>
            {title || (!immediate ? <>&nbsp;</> : null)} {/* Retain height when missing title and immediate (we can still render an aside). */}
            <div className={cls([styles.immediate, immediateClassName])} style={immediateStyle}>
              {immediate}
            </div>
          </h1>
        )
      }
      {
        //  If busy is provided, then keep a consistent height to avoid having the view skip.
        subtitle || isBusyVisible || pagination || forceSubtitleElement ? (
          <p
            className={cls([styles.subtitle, !isTitleEditable ? styles.visible : null])}
            style={{ marginBottom: 0 }}>
            {subtitle}
            {
              paginationPosition === PaginationPosition.Compact ? (
                pagination
              ) : null
            }
            {
              isBusyVisible ? (
                <TaskIndicator
                  className={cls([
                    styles.taskIndicator,
                    !subtitle && !forceSubtitleElement ? styles.noSubtitle : null
                  ])}
                  type="tiny" />
              ) : null
            }
          </p>
        ) : null
      }
      {
        aside || pagination || asideBeforePagination ? (
          <div className={styles.aside}>
            {asideBeforePagination}
            {
              paginationPosition === PaginationPosition.Aside ? (
                pagination
              ) : null
            }
            {aside}
          </div>
        ) : null
      }
    </header>
  )
}